import { Box, Stack } from '@mui/material';
import { SearchInput } from '../common/SearchInput';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import pluralize from 'pluralize';
import { SortDisplayTypes, SweepSort } from '../common/SweepSort';
import { CompleteSetupToDeploy } from '../common/deploy-button/complete-sf-setup/CompleteSetupToDeploy';
import { AutomationsList } from './AutomationsList';
import { AutomationType } from '../../types/enums/AutomationType';
import { AutomationFormVariant } from '../../types/enums/AutomationFormVariant';
import { useCallback, useState, useMemo, memo } from 'react';
import {
  AutomationSortOption,
  buildActionsMenuArray,
  SEARCH_BOX_WIDTH,
  sortAutomations,
} from './helper';
import { useAutomationFilters } from './filters/useAutomationFilters';
import AutomationsTable from './AutomationsTable';
import { useAutomationsContext } from './AutomationsContext';
import useConfirm from '../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { usePermissions } from '../../permissions/usePermissions';
import { useSelector } from 'react-redux';
import {
  selectDefaultCreationEnvironment,
  selectProductionCrmOrg,
} from '../pages/environments/environmentsReducer';
import { Commands } from '../../types/enums/Common';
import ConfirmDestructiveDialog from '../common/dialogs/ConfirmDestructiveDialog';
import useCheckManagedPackage from '../common/install-managed-package/useCheckManagedPackage';
import { useIsSetupCompleted } from '../../hooks/useIsSetupCompleted';
import { InfoDialog } from '../common/dialogs/InfoDialog';
import { CompleteSfSetupForDeployDialog } from '../common/deploy-button/complete-sf-setup/CompleteSfSetupForDeployDialog';
import { SortOrder } from '../common/types';
import { globalSearchSx } from '../pages/canvas-pages/canvasPanelConsts';
import AutomationOrderDialog from './AutomationOrderDialog';
import { AuditReportType, AuditTable } from '../common/audit-table/AuditTable';
import { ViewInSalesforceButton } from './view-in-sf-button/ViewInSalesforceButton';
import { AutomationVersionHistoryDialog } from './version-history/AutomationVersionHistoryDialog';
import { useGetSidePadding } from '../pages/configuration-canvas-panel/useGetSidePadding';
import ErrorBoundary from '../../ErrorBoundary';
import { AutomationsFilters } from './filters/AutomationFilters';
import { Filters } from '../common/filters/filters-renderer/types';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';

const automationsCountTextMap = {
  [AutomationFormVariant.ASSIGNMENT_RULES]: 'Assignment',
  [AutomationFormVariant.PLAYBOOK_ALERT]: 'Alert',
  [AutomationFormVariant.DEDUP_MATCHING]: 'Automation',
  [AutomationFormVariant.AUTOMATIONS]: 'Automation',
  [AutomationFormVariant.SCHEDULED_ASSIGNMENTS]: 'Scheduled assignment',
  [AutomationFormVariant.SCHEDULED_REPORTS]: 'Scheduled report',
  [AutomationFormVariant.SCHEDULED_JOBS]: 'Scheduled job',
  [AutomationFormVariant.MARKETING_ATTRIBUTIONS]: 'Campaign attribution model',
};

const NoSearchResult = memo(({ clearFilters }: { clearFilters: () => void }) => {
  const sidePadding = useGetSidePadding();
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      mt={2}
      sx={{
        padding: `0 ${sidePadding}px`,
      }}
    >
      <Typography variant="body" color={colors.grey[800]}>
        No results found
      </Typography>
      <Button size="medium" variant="flat-blue" onClick={clearFilters}>
        Clear filters
      </Button>
    </Box>
  );
});

interface AutomationsListWithSortProps {
  automations: AutomationStructureNew[];
  crmOrgId: string;
  closeAutomationDialog: () => void;
  onDeployAutomation: (automationItem: AutomationStructureNew, crmOrgIds: string[]) => any;
  onToggleActivation?: (
    automationJson: AutomationStructureNew,
    deployToOrgIds: string[],
    isActive: boolean,
  ) => any;
  onDeleteAutomation: (automationId: string, automationType: AutomationType) => any;
  hideSort?: boolean;
  getNewButtonWithTemplates: () => JSX.Element;
  onCreateData?: { newElement: Partial<AutomationStructureNew> };
  sortOptions?: AutomationSortOption[];
  filters: Filters;
  isExpandedMode: boolean;
  onDuplicateAutomation: (automationItem: AutomationStructureNew) => void;
  onEditAutomation: (automationItem: AutomationStructureNew) => void;
  viewInSalesforceLink?: string;
}

const AutomationsListWithSort = memo(
  ({
    automations,
    crmOrgId,
    closeAutomationDialog,
    onDeployAutomation,
    onToggleActivation,
    onDuplicateAutomation,
    onEditAutomation,
    onDeleteAutomation,
    hideSort,
    getNewButtonWithTemplates,
    onCreateData,
    sortOptions,
    filters,
    isExpandedMode,
    viewInSalesforceLink,
  }: AutomationsListWithSortProps) => {
    const dataByVariant = useAutomationsContext();
    const { automationVariant, automationJson, setAutomationJson } = dataByVariant;

    const onSortCb = useCallback((sortBy: string, sortOrder: SortOrder) => {
      setSortBy(sortBy);
      setSortOrder(sortOrder);
    }, []);

    const onEdit = useCallback(
      (automation: AutomationStructureNew) => onEditAutomation(automation),
      [onEditAutomation],
    );

    const isAutomationInEdit = useMemo(
      () => Object.keys(automationJson)?.length > 0,
      [automationJson],
    );

    const selectedId = useMemo(() => automationJson?.automationId, [automationJson?.automationId]);

    const { evaluatePermissions } = usePermissions();
    const defaultCrmOrgId = useSelector(selectDefaultCreationCrmOrgId) ?? '';
    const [isAllowedCreate, isAllowedEdit, isAllowedDeploy, isAllowedDelete] = evaluatePermissions([
      {
        permission: [
          dataByVariant.permissions.create, dataByVariant.permissions.edit,
          dataByVariant.permissions.deploy, dataByVariant.permissions.delete],
        crmOrgId: defaultCrmOrgId
      },
    ]);

    const productionEnvironment = useSelector(selectProductionCrmOrg);
    const defaultCreationEnvironment = useSelector(selectDefaultCreationEnvironment);
    const { openConfirm, onCancel, onConfirm } = useConfirm();
    const [search, setSearch] = useState('');
    const [automationForOrderDialog, setAutomationForOrderDialog] =
      useState<AutomationStructureNew>();
    const [automationForVersionHistoryDialog, setAutomationForVersionHistoryDialog] =
      useState<AutomationStructureNew>();
    const sidePadding = useGetSidePadding();

    const { filteredAutomations, clearFilters } = useAutomationFilters({
      automations,
      search,
      setSearch: (txt: string) => setSearch(txt),
    });

    //"sortBy" can be a value from the list's dropdown select, or from the one of the tables sortable columns
    const [sortBy, setSortBy] = useState(sortOptions ? sortOptions[0].value : undefined);
    const [sortOrder, setSortOrder] = useState(SortOrder.ASC); //relevant only to the table

    const selectedSortListOption = useMemo(
      () => sortOptions?.find((option) => option.value === sortBy),
      [sortOptions, sortBy],
    );

    const hideCount = useMemo(
      () => automationVariant === AutomationFormVariant.MARKETING_ATTRIBUTIONS,
      [automationVariant],
    );

    const hideTriggerText = useMemo(
      () => automationVariant === AutomationFormVariant.MARKETING_ATTRIBUTIONS,
      [automationVariant],
    );

    const automationsToDisplay = useMemo(() => {
      return sortAutomations({
        sortBy: sortBy,
        automations: filteredAutomations,
        selectedSortOption: selectedSortListOption ?? {
          dataAccessor: sortBy,
        }, //if "sortBy" is not one of the sort options, it means it is coming from the table, so we need to add a "dataAccessor" (for the sort logic)
        sortOrder: selectedSortListOption ? SortOrder.DESC : sortOrder,
      });
    }, [sortBy, filteredAutomations, selectedSortListOption, sortOrder]);

    const areAnyAutomationsDeployed = useMemo(
      () => automationsToDisplay.some((automation) => automation.status === 'Deployed'),
      [automationsToDisplay],
    );

    const hasFilters = useMemo(() => Object.keys(filters).length > 0, [filters]);

    const hasAutomationsToDisplay = useMemo(
      () => automationsToDisplay.length > 0,
      [automationsToDisplay.length],
    );

    const hasAutomationsToDisplayBoolean = useMemo(
      () => automationsToDisplay.length === 0,
      [automationsToDisplay.length],
    );

    const searchJsx = useMemo(
      () => (
        <SearchInput
          TextFieldProps={{
            disabled: isAutomationInEdit,
            value: search,
            placeholder: 'Search',
            onChange: (e) => {
              setSearch(e.target.value);
            },
            fullWidth: true,
          }}
          withFixedMagnifyingGlassIcon={true}
          onClearButtonClick={() => setSearch('')}
          size="small"
        />
      ),
      [isAutomationInEdit, search],
    );

    const memoizedGetNewButtonWithTemplates = useCallback(() => {
      if (onCreateData) {
        return (
          <Button
            disabled={isAutomationInEdit}
            startIconName="Plus"
            size="small"
            onClick={() => {
              setAutomationJson(onCreateData.newElement);
            }}
          >
            New
          </Button>
        );
      }
      return getNewButtonWithTemplates();
    }, [onCreateData, getNewButtonWithTemplates, isAutomationInEdit, setAutomationJson]);

    const viewInSalesforceButtonJsx = useMemo(() => {
      if (!viewInSalesforceLink) return null;
      return (
        <ViewInSalesforceButton
          viewInSalesforceLink={viewInSalesforceLink}
          disabled={!areAnyAutomationsDeployed}
        />
      );
    }, [viewInSalesforceLink, areAnyAutomationsDeployed]);

    const onDeploy = useCallback(
      (automationItem: AutomationStructureNew, crmOrgIds: string[]) => {
        onDeployAutomation(automationItem, crmOrgIds);
      },
      [onDeployAutomation],
    );

    const onDelete = useCallback(
      (automationId: string, automationType: AutomationType) => {
        if (selectedId === automationId) {
          closeAutomationDialog();
        }
        onDeleteAutomation(automationId, automationType);
      },
      [selectedId, closeAutomationDialog, onDeleteAutomation],
    );

    const onToggle = useCallback(
      (automationJson: AutomationStructureNew, deployToOrgIds: string[], isActive: boolean) => {
        onToggleActivation && onToggleActivation(automationJson, deployToOrgIds, isActive);
      },
      [onToggleActivation],
    );

    const canDeploy = useIsSetupCompleted();

    const { getIsManagedPackagedInstalledWithRecheck } = useCheckManagedPackage();
    const [completeSfSetupDialog, setCompleteSfSetupDialog] = useState<{
      isOpen: boolean;
      crmOrgIdsToDeploy: string[];
      automationItem: AutomationStructureNew;
    }>();

    const [automationForSlackLog, setAutomationForSlackLog] = useState<AutomationStructureNew>();
    const [automationForActivityLog, setAutomationForActivityLog] =
      useState<AutomationStructureNew>();
    const [automationForMsTeamsActivityLog, setAutomationForMsTeamsActivityLog] =
      useState<AutomationStructureNew>();

    const deployOrShowDialog = useCallback(
      async (automationItem: AutomationStructureNew, crmOrgIds: string[]) => {
        getIsManagedPackagedInstalledWithRecheck({
          withPermissionsCheck: true,
        });
        if (canDeploy) {
          onDeploy(automationItem, crmOrgIds);
        } else {
          setCompleteSfSetupDialog({
            isOpen: true,
            crmOrgIdsToDeploy: crmOrgIds,
            automationItem,
          });
        }
      },
      [canDeploy, getIsManagedPackagedInstalledWithRecheck, onDeploy],
    );

    const getActionsMenuArray = useCallback(
      (automation: AutomationStructureNew) => {
        return buildActionsMenuArray({
          automation,
          isAllowedCreate,
          isAllowedEdit,
          isAllowedDeploy,
          isAllowedDelete,
          productionEnvironment,
          defaultCreationEnvironment,
        });
      },
      [
        defaultCreationEnvironment,
        isAllowedCreate,
        isAllowedDelete,
        isAllowedDeploy,
        isAllowedEdit,
        productionEnvironment,
      ],
    );

    const onActionMenuClick = useCallback(
      (automationItem: AutomationStructureNew) => async (actionName: string) => {
        switch (actionName) {
          case Commands.Integration_Activity:
            setAutomationForActivityLog(automationItem);
            break;
          case Commands.MsTeams_Activity:
            setAutomationForMsTeamsActivityLog(automationItem);
            break;
          case Commands.Slack_Activity:
            setAutomationForSlackLog(automationItem);
            break;
          case Commands.Edit:
            onEditAutomation && onEditAutomation(automationItem);
            break;
          case Commands.Duplicate:
            const newItem: AutomationStructureNew = {
              ...automationItem,
              automationId: '',
              name: 'Copy of ' + automationItem.name,
            };
            onDuplicateAutomation(newItem);
            break;
          case Commands.Delete:
            const isConfirmed = await openConfirm(
              <ConfirmDestructiveDialog
                open={true}
                onConfirm={onConfirm}
                onClose={onCancel}
                title="Are you sure you want to delete?"
                confirmText="Delete"
                cancelText="Cancel"
              >
                {(automationItem.status === 'Deployed' || automationItem?.deployedVersionId) &&
                  'Deleting this automation will remove it from all environments immediately '}
              </ConfirmDestructiveDialog>,
            );
            if (isConfirmed) {
              onDelete(automationItem.automationId, automationItem.type);
            }
            break;
          case 'onToggleDefault':
            onToggle(
              automationItem,
              [defaultCreationEnvironment?.id ?? ''],
              !automationItem.isActive,
            );
            break;
          case 'onToggleBoth':
            onToggle(
              automationItem,
              [defaultCreationEnvironment?.id ?? '', productionEnvironment?.id ?? ''],
              !automationItem.isActive,
            );
            break;
          case 'onDeployDefault':
            deployOrShowDialog(automationItem, [defaultCreationEnvironment?.id ?? '']);
            break;
          case 'onDeployBoth':
            deployOrShowDialog(automationItem, [
              defaultCreationEnvironment?.id ?? '',
              productionEnvironment?.id ?? '',
            ]);
            break;
          case Commands.Order:
            setAutomationForOrderDialog(automationItem);
            break;
          case Commands.Version_History:
            setAutomationForVersionHistoryDialog(automationItem);
            break;
        }
      },
      [
        defaultCreationEnvironment?.id,
        deployOrShowDialog,
        onCancel,
        onConfirm,
        onDelete,
        onDuplicateAutomation,
        onEditAutomation,
        onToggle,
        openConfirm,
        productionEnvironment?.id,
        setAutomationForOrderDialog,
        setAutomationForVersionHistoryDialog,
      ],
    );

    const memoizedGetActionsMenu = useCallback(
      (automation: AutomationStructureNew) => {
        return getActionsMenuArray(automation);
      },
      [getActionsMenuArray],
    );

    const memoizedOnActionMenuClick = useCallback(
      (automationItem: AutomationStructureNew) => async (actionName: string) => {
        return onActionMenuClick(automationItem)(actionName);
      },
      [onActionMenuClick],
    );

    const renderTableContent = () => (
      <Stack
        className="table-jsx"
        sx={{
          gap: isExpandedMode ? 3 : 2,
          height: '100%',
          padding: `0 ${sidePadding}px`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            ...globalSearchSx(isExpandedMode),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: SEARCH_BOX_WIDTH }}>
              {searchJsx}
            </Box>
            <ErrorBoundary fallback={() => <div>Error in filters</div>}>
              <AutomationsFilters forceCompact={!isExpandedMode} filters={filters} />
            </ErrorBoundary>
          </Box>
          <Stack direction="row" gap={2} alignItems="center">
            {viewInSalesforceButtonJsx}
            {memoizedGetNewButtonWithTemplates()}
          </Stack>
        </Box>
        <CompleteSetupToDeploy entityName={dataByVariant.nameInMessage} />
        <AutomationsTable
          crmOrgId={crmOrgId}
          onSortCb={onSortCb}
          automations={automationsToDisplay}
          getActionsMenu={memoizedGetActionsMenu}
          onActionMenuClick={memoizedOnActionMenuClick}
          onEdit={onEdit}
          onClearFilters={clearFilters}
        />
      </Stack>
    );

    const renderListContent = () => (
      <>
        <Box
          className="list-jsx"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: `0 ${sidePadding}px`,
            ...globalSearchSx(isExpandedMode),
            borderRadius: 0,
          }}
        >
          <Box flex={1}>{searchJsx}</Box>
          <Box>{memoizedGetNewButtonWithTemplates()}</Box>
        </Box>

        {!hideSort && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: `0 ${sidePadding}px `,
            }}
          >
            {!hideCount && (
              <Typography variant="body" color={colors.grey[700]}>
                {pluralize(
                  automationsCountTextMap[automationVariant],
                  automationsToDisplay.length,
                  true,
                )}
              </Typography>
            )}
            {viewInSalesforceButtonJsx}
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
              {!isExpandedMode && hasFilters && (
                <ErrorBoundary>
                  <AutomationsFilters forceCompact={!isExpandedMode} filters={filters} />
                </ErrorBoundary>
              )}
              {sortOptions && sortBy && (
                <SweepSort
                  selectedValue={sortBy}
                  onChange={setSortBy}
                  options={sortOptions}
                  displayType={SortDisplayTypes.IconAndLabel}
                />
              )}
            </Box>
          </Box>
        )}

        {hasAutomationsToDisplayBoolean && <NoSearchResult clearFilters={clearFilters} />}

        {hasAutomationsToDisplay && (
          <Stack
            className="automations-list"
            sx={{
              height: '100%',
              overflow: 'hidden',
              flex: 1,
            }}
          >
            <CompleteSetupToDeploy entityName={dataByVariant.nameInMessage} />
            <AutomationsList
              automations={automationsToDisplay}
              crmOrgId={crmOrgId}
              selectedId={selectedId}
              getActionsMenu={memoizedGetActionsMenu}
              onActionMenuClick={memoizedOnActionMenuClick}
              onEdit={onEdit}
              noTriggerText={hideTriggerText}
            />
          </Stack>
        )}
      </>
    );

    return (
      <>
        {isExpandedMode ? renderTableContent() : renderListContent()}
        {automationForSlackLog && (
          <InfoDialog
            open={automationForSlackLog !== undefined}
            handleClose={() => {
              setAutomationForSlackLog(undefined);
            }}
            PaperPropsSx={{ paddingBottom: '16px', width: '1246px' }}
            showCloseButton
            titleJsx={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant={'h2'}>Slack activity</Typography>
                <Typography variant={'h4'}>{automationForSlackLog.name}</Typography>
              </Box>
            }
          >
            <AuditTable auditType={AuditReportType.SLACK} automation={automationForSlackLog} />
          </InfoDialog>
        )}

        {automationForActivityLog && (
          <InfoDialog
            open={automationForActivityLog !== undefined}
            handleClose={() => {
              setAutomationForActivityLog(undefined);
            }}
            PaperPropsSx={{ paddingBottom: '16px', width: '1246px' }}
            showCloseButton
            titleJsx={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant={'h2'}>Integration activity</Typography>
                <Typography variant={'h4'}>{automationForActivityLog.name}</Typography>
              </Box>
            }
          >
            <AuditTable
              auditType={AuditReportType.AUTOMATION_WEBHOOK}
              automation={automationForActivityLog}
            />
          </InfoDialog>
        )}

        {automationForMsTeamsActivityLog && (
          <InfoDialog
            open={automationForMsTeamsActivityLog !== undefined}
            handleClose={() => {
              setAutomationForMsTeamsActivityLog(undefined);
            }}
            PaperPropsSx={{ paddingBottom: '16px', width: '1246px' }}
            showCloseButton
            titleJsx={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant={'h2'}>MS Teams activity</Typography>
                <Typography variant={'h4'}>{automationForMsTeamsActivityLog.name}</Typography>
              </Box>
            }
          >
            <AuditTable
              auditType={AuditReportType.MS_TEAMS}
              automation={automationForMsTeamsActivityLog}
            />
          </InfoDialog>
        )}

        {completeSfSetupDialog?.isOpen && (
          <CompleteSfSetupForDeployDialog
            cancelDialog={async () => {
              setCompleteSfSetupDialog(undefined);
            }}
            onCompleteItemsCallback={async () => {
              setCompleteSfSetupDialog(undefined);
              await onDeploy(
                completeSfSetupDialog.automationItem,
                completeSfSetupDialog.crmOrgIdsToDeploy,
              );
            }}
            entityName={dataByVariant.nameInMessage}
          />
        )}
        {automationForOrderDialog && (
          <AutomationOrderDialog
            readonly={!isAllowedEdit}
            crmOrgId={crmOrgId}
            automation={automationForOrderDialog}
            closeDialog={() => {
              setAutomationForOrderDialog(undefined);
            }}
          />
        )}
        {automationForVersionHistoryDialog && (
          <AutomationVersionHistoryDialog
            automation={automationForVersionHistoryDialog}
            closeDialog={() => setAutomationForVersionHistoryDialog(undefined)}
            onEdit={(automation) => {
              setAutomationForVersionHistoryDialog(undefined);
              onEditAutomation(automation);
            }}
          />
        )}
      </>
    );
  },
);

export default AutomationsListWithSort;
